import { useCallback, useEffect, useRef, useState } from 'react';

// Ein Symbol statt eines Strings: `target` wird gegen Kategorienamen verglichen, und eine
// Nutzer-Kategorie darf jeden Namen tragen — auch „auto". Der Wert wird nur verglichen, nie
// serialisiert (kein data-Attribut, kein React-Key).
export const EXTERNAL_DROP_AUTO = Symbol('external-drop-auto');
const TEXT_TYPES = ['text/uri-list', 'text/plain', 'text/html'];
const WACHHUND_MS = 150;

function typen(transfer) {
  return Array.from(transfer?.types || []).map((t) => String(t).toLowerCase());
}
const hatText = (transfer) => typen(transfer).some((t) => TEXT_TYPES.includes(t));
const hatUriList = (transfer) => typen(transfer).includes('text/uri-list');

/**
 * Nimmt Karten an, die als HTML5-Drag von außen kommen (fremde Seite, zweiter Tab). dnd-kit
 * sieht solche Züge nicht; deshalb hängen native Handler am Seiten-Wrapper, an jedem
 * Kategorie-Container und an der Leiste „Automatisch".
 *
 * Hervorhebung nur bei `text/uri-list`: während `dragover` ist nur der Typ lesbar, und
 * markierter Text (`text/plain`) würde sonst jede Kategorie aufleuchten lassen. `preventDefault`
 * dagegen für jeden Texttyp — ohne es feuert der Browser kein `drop`, und der Drop wertet jede
 * Nutzlast aus.
 *
 * Kein Tiefenzähler und keine `dragleave`-Auswertung: `dragleave` feuert bei jedem Wechsel auf
 * ein Kind-Element, `relatedTarget` ist dabei in Chromium unzuverlässig, und endet der Zug
 * außerhalb des Fensters, kommt gar nichts an. Stattdessen hält jedes `dragover` einen
 * Wachhund am Leben; bleibt es 150 ms aus, ist der Zug vorbei oder woanders.
 *
 * Ob der Zeiger neben allen Kategorien steht, entscheidet eine Ereignis-Markierung statt eines
 * Vergleichs von `target` oder `relatedTarget`: der Wrapper ist vollständig von Kindern bedeckt,
 * `target === currentTarget` träfe praktisch nie zu.
 */
export default function useExternalCardDrop({ enabled, onDrop }) {
  const [active, setActive] = useState(false);
  const [target, setTarget] = useState(null);
  const wachhund = useRef(null);
  const kategorieTreffer = useRef(false);

  const reset = useCallback(() => {
    if (wachhund.current) clearTimeout(wachhund.current);
    wachhund.current = null;
    setActive(false);
    setTarget(null);
  }, []);

  const lebenszeichen = useCallback(() => {
    if (wachhund.current) clearTimeout(wachhund.current);
    wachhund.current = setTimeout(reset, WACHHUND_MS);
  }, [reset]);

  useEffect(() => () => { if (wachhund.current) clearTimeout(wachhund.current); }, []);

  const rootProps = {
    onDragOver: (e) => {
      if (!enabled || !hatText(e.dataTransfer)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      if (!hatUriList(e.dataTransfer)) return;
      setActive(true);
      // Der Kind-Handler läuft vor dem Wrapper: gesetzt heißt, dieses Ereignis kam von einer Kategorie oder der Leiste.
      if (!kategorieTreffer.current) setTarget(null);
      kategorieTreffer.current = false;
      lebenszeichen();
    },
    onDrop: (e) => {
      if (!enabled) return;
      e.preventDefault();
      const transfer = e.dataTransfer;
      reset();
      onDrop(null, transfer);
    },
  };

  const propsFor = useCallback((key, categoryId) => ({
    onDragOver: (e) => {
      if (!enabled || !hatText(e.dataTransfer)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      if (!hatUriList(e.dataTransfer)) return;
      // Nicht stoppen: der Wrapper hält über dasselbe Ereignis den Wachhund am Leben.
      setActive(true);
      kategorieTreffer.current = true;
      setTarget(key);
      lebenszeichen();
    },
    onDrop: (e) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();
      const transfer = e.dataTransfer;
      reset();
      onDrop(categoryId, transfer);
    },
  }), [enabled, onDrop, reset, lebenszeichen]);

  return { active, target, rootProps, propsFor };
}
